import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import QuickSearch from "./QuickSearch";
import {
  Eye, LayoutDashboard, Users, Glasses, Megaphone, Activity, Settings, LogOut, ShieldCheck, UserCog,
} from "lucide-react";
import { Button } from "./ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/clienti", label: "Clienti", icon: Users },
  { to: "/dati-ottici", label: "Dati Ottici", icon: Glasses },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/log", label: "Log Attività", icon: Activity, adminOnly: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, adminOnly: true },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0F172A] text-white flex-col hidden md:flex">
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg bg-[#1E40AF] flex items-center justify-center">
            <Eye className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-head font-bold tracking-tight leading-none">Ottica CRM</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">GDPR Ready</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1E40AF] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 mb-2">
            <div className="h-9 w-9 rounded-full bg-[#1E40AF] flex items-center justify-center shrink-0">
              {isAdmin ? <ShieldCheck className="h-4 w-4" /> : <UserCog className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.username}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">{isAdmin ? "Amministratore" : "Operatore"}</div>
            </div>
          </div>
          <Button data-testid="logout-button" onClick={doLogout} variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5">
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-4 px-4 md:px-8 sticky top-0 z-40">
          <QuickSearch />
          <div className="ml-auto flex items-center gap-3">
            <span
              data-testid="role-badge"
              className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                isAdmin ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-sky-50 text-sky-700 border border-sky-200"
              }`}
            >
              {isAdmin ? <ShieldCheck className="h-3.5 w-3.5" /> : <UserCog className="h-3.5 w-3.5" />}
              {isAdmin ? "AMMINISTRATORE" : "OPERATORE"}
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-auto custom-scroll p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
